import React, { useCallback, useEffect, useMemo, useState } from "react";
import t from "../../../../../../../i18n";
import useGroups from "../../../../../../../api/useGroups";
import type { Source } from "../../../../../../../api/useSources";
import type { Project } from "../../../../../../../api/useProjects";
import DateRangePicker from "@ui/DateRangePicker/DateRangePicker";
import {
  parseAbsolute,
  toCalendarDateTime,
  toZoned,
  type DateValue,
} from "@internationalized/date";
import * as io from "io-ts";
import { Range } from "../../../../../Dashboards/schema/period";
import { withMessage } from "io-ts-types";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import type { RangeValue } from "@react-types/shared";
import { useEventPeriod } from "../../../../hooks/useEventPeriod";
import type { Event } from "../../../../../../../api/useEvents";
import type { ActiveAnalysis } from "../../../../../../../api/useActiveAnalysesStream";
import { baseAggregationPeriodPresets } from "../../../../../../../domain/aggregation/preset";

const datasetRangeMessage = css`
  margin-top: ${spacings.xs};
`;

const dateRangePickerLayout = css`
  display: flex;
  flex-direction: column;
  max-width: 300px;
`;

const durationKeys = {
  ALL: null,
  CYCLE: null,
  P3M: null,
  P3W: null,
  P1Y: null,
};
export const Duration = withMessage(io.keyof(durationKeys), () =>
  t("schema.period", Object.keys(durationKeys).join(", ")),
);
export const Period = io.union([Range, Duration]);
export type PeriodT = io.TypeOf<typeof Period>;

interface P1 {
  project: Project;
  source: Source;
  analyses: ActiveAnalysis[];
  boundaries: "active-analyses";
  event: Event;
}
interface P2 {
  project: Project;
  source: Source;
  analyses?: never;
  boundaries: "analyses";
  event: Event;
}

type PeriodSelectorProps = P1 | P2;

const PeriodSelector = ({ event, source, project, analyses, boundaries }: PeriodSelectorProps) => {
  const [dateTimeError, setDateTimeError] = useState<string>();
  const [dateTimeInfo, setDateTimeInfo] = useState<string>();

  const [period, setPeriod, extremes] = useEventPeriod(
    boundaries === "active-analyses"
      ? { event, source, project, analyses, boundaries: "active-analyses" }
      : { event, source, project, boundaries: "analyses" },
  );

  const dateBoundaries = useMemo(
    () => ({
      startDate: parseAbsolute(new Date(extremes.from).toISOString(), project.timezone),
      endDate: parseAbsolute(new Date(extremes.to).toISOString(), project.timezone),
    }),
    [extremes, project.timezone],
  );

  const [dateRangeValue, setDateRangeValue] = useState(() => ({
    start: parseAbsolute(new Date(period.from).toISOString(), project.timezone),
    end: parseAbsolute(new Date(period.to).toISOString(), project.timezone),
  }));

  const groups = useGroups(project.id);
  const presets = baseAggregationPeriodPresets({
    limit: { from: new Date(extremes.from), to: new Date() },
    timezone: project.timezone,
    groups,
  });

  const onDateValueChange = useCallback(
    (value: RangeValue<DateValue> | null) => {
      setDateTimeError(undefined);
      setDateTimeInfo(undefined);

      if (!value) {
        const preset = presets.find(p => p.metadata?.["id"] === "ALL");
        setDateRangeValue(
          preset
            ? {
                start: toZoned(
                  typeof preset.startDate === "function" ? preset.startDate() : preset.startDate,
                  project.timezone,
                ),
                end: toZoned(
                  typeof preset.endDate === "function" ? preset.endDate() : preset.endDate,
                  project.timezone,
                ),
              }
            : {
                start: toZoned(dateBoundaries.startDate, project.timezone),
                end: toZoned(dateBoundaries.endDate, project.timezone),
              },
        );
        setPeriod({
          from: toCalendarDateTime(dateBoundaries.startDate).toDate(project.timezone).getTime(),
          to: toCalendarDateTime(dateBoundaries.endDate).toDate(project.timezone).getTime(),
        });
        return;
      }

      setDateRangeValue({
        start: toZoned(value.start, project.timezone),
        end: toZoned(value.end, project.timezone),
      });
      setPeriod({
        from: value.start.toDate(project.timezone).getTime(),
        to: value.end.toDate(project.timezone).getTime(),
      });
    },
    [presets, dateBoundaries, project.timezone, setPeriod],
  );

  useEffect(() => {
    setDateRangeValue({
      start: parseAbsolute(new Date(period.from).toISOString(), project.timezone),
      end: parseAbsolute(new Date(period.to).toISOString(), project.timezone),
    });
  }, [period, project.timezone]);

  return (
    <div className={dateRangePickerLayout}>
      <DateRangePicker
        id=""
        presets={presets}
        popoverPlacement="bottom end"
        dateOptions={{
          "aria-label": "Fault event period",
          shouldCloseOnSelect: true,
          hideTimeZone: true,
          granularity: "day",
          minValue: dateBoundaries?.startDate,
          maxValue: dateBoundaries?.endDate,
          value: dateRangeValue,
          isDateUnavailable: date => {
            if (dateBoundaries?.startDate && date.compare(dateBoundaries?.startDate) < 0)
              return true;
            if (dateBoundaries?.endDate && date.compare(dateBoundaries?.endDate) > 0) return true;
            return false;
          },
          onChange: onDateValueChange,
        }}
      />
      {dateTimeError && (
        <AdditionnalMessage className={datasetRangeMessage} status="ERROR" text={dateTimeError} />
      )}
      {dateTimeInfo && !dateTimeError && (
        <AdditionnalMessage className={datasetRangeMessage} status="INFO" text={dateTimeInfo} />
      )}
    </div>
  );
};

export default React.memo(PeriodSelector);
